import { Link } from "react-router-dom";
import { FileText, Shield, ClipboardCheck, FileCheck2, Calendar, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";

interface RelatedResourcesProps {
  category?: string;
  tags?: string[];
}

const toolLinks = [
  { nameKey: "blog.sidebar.soaGenerator", path: "/soa-generator", icon: FileText, keywords: ["statement of applicability", "soa", "controls", "annex a"] },
  { nameKey: "blog.sidebar.riskAssessment", path: "/tools/risk-assessment", icon: Shield, keywords: ["risk", "threat", "vulnerability", "impact"] },
  { nameKey: "blog.sidebar.gapAnalysis", path: "/tools/gap-analysis", icon: ClipboardCheck, keywords: ["gap", "compliance", "assessment", "readiness"] },
  { nameKey: "blog.sidebar.internalAudit", path: "/tools/internal-audit", icon: FileCheck2, keywords: ["audit", "checklist", "compliance", "review"] },
  { nameKey: "blog.sidebar.roadmap", path: "/tools/implementation-roadmap", icon: Calendar, keywords: ["implementation", "roadmap", "timeline", "certification"] },
];

const documentLinks = [
  { nameKey: "relatedResources.infoSecPolicy", path: "/documents#mandatory", keywords: ["policy", "security", "governance"] },
  { nameKey: "relatedResources.riskTemplate", path: "/documents#templates", keywords: ["risk", "template", "assessment"] },
  { nameKey: "relatedResources.implGuide", path: "/documents#guides", keywords: ["implementation", "guide", "getting started"] },
  { nameKey: "relatedResources.auditChecklist", path: "/documents#audit", keywords: ["audit", "checklist", "compliance"] },
];

export const RelatedResources = ({ category, tags = [] }: RelatedResourcesProps) => {
  const { t } = useLanguage();
  const searchTerms = [...(tags || []), category?.toLowerCase() || ""].filter(Boolean);
  
  // Find relevant tools based on tags/category
  const relevantTools = toolLinks.filter(tool => 
    searchTerms.some(term => 
      tool.keywords.some(keyword => keyword.includes(term.toLowerCase()) || term.toLowerCase().includes(keyword))
    )
  ).slice(0, 3);

  // Find relevant documents
  const relevantDocs = documentLinks.filter(doc =>
    searchTerms.some(term =>
      doc.keywords.some(keyword => keyword.includes(term.toLowerCase()) || term.toLowerCase().includes(keyword))
    )
  ).slice(0, 2);

  // Default tools if no matches
  const displayTools = relevantTools.length > 0 ? relevantTools : toolLinks.slice(0, 3);
  const displayDocs = relevantDocs.length > 0 ? relevantDocs : documentLinks.slice(0, 2);

  return (
    <Card className="mt-8 bg-muted/30">
      <CardHeader>
        <CardTitle className="text-lg">{t('relatedResources.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">{t('relatedResources.tools')}</h4>
          <div className="space-y-2">
            {displayTools.map(tool => (
              <Link 
                key={tool.path}
                to={tool.path}
                className="flex items-center gap-2 text-sm hover:text-primary transition-colors p-2 -mx-2 rounded-lg hover:bg-background"
              >
                <tool.icon className="h-4 w-4 text-primary" />
                {t(tool.nameKey)}
                <ArrowRight className="h-3 w-3 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">{t('relatedResources.documents')}</h4>
          <div className="space-y-2">
            {displayDocs.map(doc => (
              <Link 
                key={doc.path}
                to={doc.path}
                className="flex items-center gap-2 text-sm hover:text-primary transition-colors p-2 -mx-2 rounded-lg hover:bg-background"
              >
                <FileText className="h-4 w-4 text-muted-foreground" />
                {t(doc.nameKey)}
                <ArrowRight className="h-3 w-3 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};